var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React, { useEffect } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { Typography } from '@mui/material';
import cx from 'classnames';

var useStyles = makeStyles(function (theme) {
  return {
    root: _extends({}, theme.typography.body2),
    paper: {
      maxHeight: 320
    },
    typo: {
      whiteSpace: 'break-spaces'
    },
    disabledLabel: {
      color: theme.palette.grey[700]
    }
  };
});

var Select = function Select(_ref) {
  var id = _ref.id,
      optionsKey = _ref.optionsKey,
      _ref$value = _ref.value,
      value = _ref$value === undefined ? '' : _ref$value,
      label = _ref.label,
      _ref$items = _ref.items,
      items = _ref$items === undefined ? [] : _ref$items,
      isMultiple = _ref.isMultiple,
      onChange = _ref.onChange,
      _ref$textFieldProps = _ref.textFieldProps,
      textFieldProps = _ref$textFieldProps === undefined ? {} : _ref$textFieldProps,
      valueAccessor = _ref.valueAccessor,
      labelAccessor = _ref.labelAccessor,
      disabled = _ref.disabled,
      _ref$dataTestId = _ref.dataTestId,
      dataTestId = _ref$dataTestId === undefined ? 'select-testid' : _ref$dataTestId;

  var classes = useStyles();
  var ref = React.useRef(null);

  var _React$useState = React.useState(false),
      open = _React$useState[0],
      setOpen = _React$useState[1];

  var _React$useState2 = React.useState(0),
      width = _React$useState2[0],
      setWidth = _React$useState2[1];

  var handleChange = function handleChange(event) {
    if (R.is(Function)(onChange)) {
      onChange(event.target.value);
    }
  };

  useEffect(function () {
    if (ref.current && open) setWidth(ref.current.clientWidth);
  }, [open]);

  var getter = function getter(key) {
    return function (accessor) {
      return R.ifElse(R.is(Object), function (item) {
        return R.is(Function, accessor) ? accessor(item) : R.prop(key)(item);
      }, R.identity);
    };
  };

  var joinByValue = R.innerJoin(function (obj, value) {
    return obj.value === value;
  });
  var labels = R.pluck('label', joinByValue(items, R.is(Array, value) ? value : [value]));
  return React.createElement(
    TextField,
    _extends({
      select: true,
      id: id,
      'data-testid': dataTestId,
      disabled: R.isEmpty(items),
      label: label,
      value: value,
      title: R.join(', ', labels),
      onChange: handleChange,
      variant: 'outlined',
      margin: 'dense',
      size: 'small',
      fullWidth: true,
      SelectProps: {
        ref: ref,
        open: open,
        autoWidth: true,
        multiple: isMultiple,
        disabled: disabled,
        onOpen: function onOpen() {
          return setOpen(true);
        },
        onClose: function onClose() {
          return setOpen(false);
        },
        classes: { root: classes.root },
        renderValue: function renderValue() {
          return React.createElement(
            Typography,
            { variant: 'inherit', className: classes.typo },
            R.join(', ', labels)
          );
        },
        MenuProps: {
          getContentAnchorEl: null,
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          transformOrigin: { vertical: 'top', horizontal: 'center' },
          classes: { paper: classes.paper },
          PaperProps: {
            style: { width: width }
          }
        }
      },
      className: classes.root
    }, textFieldProps),
    R.map(function (item) {
      var _cx;

      var value = getter('value')(valueAccessor)(item);
      var label = getter('label')(labelAccessor)(item);
      return React.createElement(
        MenuItem,
        {
          key: optionsKey + '-' + value,
          id: label,
          value: value,
          dense: true,
          'aria-label': label,
          title: label,
          disabled: R.propOr(false, 'disabled')(item)
        },
        React.createElement(
          Typography,
          {
            variant: 'inherit',
            className: cx(classes.typo, (_cx = {}, _cx[classes.disabledLabel] = R.propOr(false, 'disabled')(item), _cx))
          },
          label
        )
      );
    })(items)
  );
};

Select.propTypes = process.env.NODE_ENV !== "production" ? {
  id: PropTypes.string,
  optionsKey: PropTypes.string,
  dataTestId: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
  items: PropTypes.array,
  onChange: PropTypes.func,
  textFieldProps: PropTypes.object,
  valueAccessor: PropTypes.func,
  labelAccessor: PropTypes.func,
  isMultiple: PropTypes.bool,
  disabled: PropTypes.bool
} : {};

export default Select;